import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchPublicQuestion, submitShareAnswer } from '../lib/api';

const REFERRAL_KEY = 'squishy_pending_referral';

function getFingerprint(): string {
  const key = 'squishy_fp';
  let fp = localStorage.getItem(key);
  if (!fp) {
    fp = crypto.randomUUID();
    localStorage.setItem(key, fp);
  }
  return fp;
}

type QuestionData = Awaited<ReturnType<typeof fetchPublicQuestion>>;
type AnswerResult = Awaited<ReturnType<typeof submitShareAnswer>>;

export function SharedQuestionPage() {
  const { shareId } = useParams<{ shareId: string }>();
  const [question, setQuestion] = useState<QuestionData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [result, setResult] = useState<AnswerResult | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const isSata = question?.question_type === 'sata';
  const isFlashcard = question?.question_type === 'flashcard';
  const [flashcardFlipped, setFlashcardFlipped] = useState(false);

  useEffect(() => {
    if (!shareId) return;
    localStorage.setItem(REFERRAL_KEY, shareId);
    fetchPublicQuestion(shareId)
      .then((q) => {
        setQuestion(q);
      })
      .catch(() => setError('Question not found or link has expired.'))
      .finally(() => setLoading(false));
  }, [shareId]);

  const toggleOption = (i: number) => {
    if (result) return;
    setSelected(prev => {
      const next = new Set(prev);
      if (isSata) {
        if (next.has(i)) next.delete(i);
        else next.add(i);
      } else {
        next.clear();
        next.add(i);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!shareId) return;
    if (selected.size === 0) return;
    setSubmitting(true);
    try {
      const answer = { chosen_index: isSata ? [...selected].sort() : [...selected][0]! as number };
      const res = await submitShareAnswer(shareId, answer, getFingerprint());
      setResult(res);
    } catch {
      setError('Failed to submit answer. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-[#1e1b4b] to-[#0f172a]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-teal-400 border-t-transparent" />
      </div>
    );
  }

  if (error || !question) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-[#1e1b4b] to-[#0f172a] p-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-xl">
          <img src="/logo.png" alt="Squishy School" className="mx-auto h-16 w-16" />
          <h1 className="mt-4 text-lg font-bold text-slate-900">Oops!</h1>
          <p className="mt-2 text-sm text-slate-600">{error ?? 'Question not found.'}</p>
          <Link
            to="/signup"
            className="mt-5 inline-block rounded-xl bg-teal-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-teal-700"
          >
            Create a free account
          </Link>
        </div>
      </div>
    );
  }

  // Flashcard: completely separate render
  if (isFlashcard && question) {
    return (
      <div className="flex min-h-screen flex-col bg-gradient-to-b from-[#0d9488] to-[#065f46] p-4">
        <div className="mx-auto w-full max-w-lg flex-1">
          {/* Header */}
          <div className="mb-4 flex items-center gap-3">
            <img src="/logo.png" alt="" className="h-10 w-10" />
            <div>
              <p className="text-sm font-semibold text-white">Squishy School</p>
              <p className="text-xs text-white/60">Flash Card</p>
            </div>
          </div>

          {/* Flashcard */}
          <div
            onClick={() => !flashcardFlipped && setFlashcardFlipped(true)}
            className={`min-h-[320px] rounded-2xl border-2 shadow-xl transition-all ${
              flashcardFlipped
                ? 'bg-white border-teal-200'
                : 'bg-gradient-to-br from-[#1e1b4b] to-[#0f172a] border-transparent cursor-pointer active:scale-[0.99]'
            }`}
          >
            {!flashcardFlipped ? (
              <div className="flex flex-col items-center justify-center p-6 min-h-[320px]">
                {question.topic && (
                  <span className="text-[10px] font-semibold text-white/60 uppercase tracking-wider mb-4">
                    {question.topic}
                  </span>
                )}
                <p className="text-lg font-semibold text-white text-center leading-relaxed">
                  {question.front}
                </p>
                <div className="mt-6 flex items-center gap-1.5 text-white/50 text-xs">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Tap to reveal answer
                </div>
              </div>
            ) : (
              <div className="p-6 min-h-[320px]">
                {question.topic && (
                  <span className="inline-block text-[10px] font-semibold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full uppercase tracking-wider mb-3">
                    {question.topic}
                  </span>
                )}
                <p className="text-xs font-medium text-slate-400 mb-2">Q: {question.front}</p>
                <div className="border-t border-slate-100 pt-3">
                  <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-line">
                    {question.back}
                  </p>
                </div>
                {question.category && (
                  <div className="mt-4 text-[10px] text-slate-400">
                    {question.category}
                  </div>
                )}
              </div>
            )}
          </div>

          {!flashcardFlipped && (
            <button
              onClick={() => setFlashcardFlipped(true)}
              className="mt-4 w-full rounded-xl bg-white/20 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-white/30"
            >
              Show Answer
            </button>
          )}

          {/* CTA */}
          <div className="mt-6 rounded-2xl bg-white/10 p-5 text-center backdrop-blur">
            <p className="text-sm font-semibold text-white">Want more flash cards like this?</p>
            <p className="mt-1 text-xs text-white/60">Free study tools for nursing, medical, and PA students.</p>
            <Link
              to="/signup"
              className="mt-3 inline-block rounded-xl bg-[#fbbf24] px-6 py-2.5 text-sm font-semibold text-[#1e1b4b] shadow-sm hover:bg-[#f59e0b]"
            >
              Sign up free
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const correctIndices: number[] = result && result.correct_index !== null && result.correct_index !== undefined
    ? Array.isArray(result.correct_index) ? result.correct_index : [result.correct_index]
    : [];

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-b from-[#1e1b4b] to-[#0f172a] p-4">
      <div className="mx-auto w-full max-w-lg flex-1">
        {/* Header */}
        <div className="mb-4 flex items-center gap-3">
          <img src="/logo.png" alt="" className="h-10 w-10" />
          <div>
            <p className="text-sm font-semibold text-white">Squishy School</p>
            {question.exam_name && (
              <p className="text-xs text-slate-400">{question.exam_code ?? question.exam_name}</p>
            )}
          </div>
        </div>

        {/* Question card */}
        <div className="rounded-2xl bg-white p-5 shadow-xl">
          <div className="mb-2 flex items-center gap-2">
            {question.topic && (
              <p className="text-xs font-medium text-teal-600">{question.topic}{question.subtopic ? ` / ${question.subtopic}` : ''}</p>
            )}
            {isSata && (
              <span className="ml-auto rounded-full bg-amber-100 dark:bg-amber-500/20 px-2.5 py-0.5 text-[11px] font-bold text-amber-700">
                Select All That Apply
              </span>
            )}
          </div>

          <h2 className="text-[15px] font-semibold leading-relaxed text-slate-900 whitespace-pre-wrap">
            {question.question}
          </h2>

          {/* Options */}
          <div className="mt-4 space-y-2">
            {(question.options ?? []).map((opt, i) => {
                  const letter = String.fromCharCode(65 + i);
                  const isSelected = selected.has(i);
                  let optClass = 'border-slate-200 bg-white hover:border-teal-400';

                  if (result) {
                    if (correctIndices.includes(i)) {
                      optClass = 'border-emerald-400 bg-emerald-50';
                    } else if (isSelected && !correctIndices.includes(i)) {
                      optClass = 'border-red-400 bg-red-50';
                    } else {
                      optClass = 'border-slate-100 bg-slate-50 opacity-60';
                    }
                  } else if (isSelected) {
                    optClass = 'border-teal-500 bg-teal-50 ring-1 ring-teal-500';
                  }

                  const badgeClass = result && correctIndices.includes(i)
                    ? 'bg-emerald-500 text-white'
                    : result && isSelected && !correctIndices.includes(i)
                    ? 'bg-red-500 text-white'
                    : isSelected
                    ? 'bg-teal-500 text-white'
                    : 'bg-slate-100 text-slate-600';

                  return (
                    <button
                      key={i}
                      disabled={!!result}
                      onClick={() => toggleOption(i)}
                      className={`flex w-full items-start gap-3 rounded-xl border-2 p-3 text-left transition ${optClass}`}
                    >
                      <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center ${isSata ? 'rounded-md' : 'rounded-full'} text-xs font-bold ${badgeClass}`}>
                        {result && correctIndices.includes(i) ? '\u2713' :
                         result && isSelected ? '\u2717' :
                         isSelected && isSata ? '\u2713' : letter}
                      </span>
                      <span className="text-sm text-slate-800">{opt}</span>
                    </button>
                  );
                })}
          </div>

          {/* Submit button */}
          {!result && (
            <button
              onClick={handleSubmit}
              disabled={selected.size === 0 || submitting}
              className="mt-4 w-full rounded-xl bg-teal-600 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700 disabled:opacity-40"
            >
              {submitting ? 'Checking...' : 'Submit Answer'}
            </button>
          )}

          {/* Result */}
          {result && (
            <div className={`mt-4 rounded-xl p-4 ${result.correct ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
              <p className={`text-sm font-bold ${result.correct ? 'text-emerald-700' : 'text-red-700'}`}>
                {result.correct ? 'Correct!' : 'Incorrect'}
              </p>
              {result.brief_explanation && (
                <p className="mt-2 text-sm leading-relaxed text-slate-700">{result.brief_explanation}</p>
              )}
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="mt-6 rounded-2xl bg-white/10 p-5 text-center backdrop-blur">
          <p className="text-sm font-semibold text-white">Want more questions like this?</p>
          <p className="mt-1 text-xs text-slate-300">Free study tools for nursing, medical, and PA students.</p>
          <Link
            to="/signup"
            className="mt-3 inline-block rounded-xl bg-teal-500 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-teal-600"
          >
            Sign up free
          </Link>
        </div>
      </div>
    </div>
  );
}
